/**
 * useRecording Hook
 * Handles audio recording from microphone
 */

import { useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';

/**
 * Generate waveform data from AudioBuffer
 */
const generateWaveform = (audioBuffer, numSamples = 200) => {
  const channelData = audioBuffer.getChannelData(0);
  const blockSize = Math.floor(channelData.length / numSamples);
  const waveform = [];

  for (let i = 0; i < numSamples; i++) {
    const start = i * blockSize;
    let sum = 0;
    for (let j = 0; j < blockSize; j++) {
      sum += Math.abs(channelData[start + j] || 0);
    }
    waveform.push(sum / blockSize);
  }

  const max = Math.max(...waveform);
  return waveform.map(v => v / (max || 1));
};

/**
 * Hook for audio recording
 */
export function useRecording({
  audioContextRef,
  tracks,
  setTracks,
  selectedTrack,
  pushUndoAction,
  ActionTypes,
  countInEnabled,
  countInBeats,
  performCountIn,
  bpm,
  timeSignature,
}) {
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTrack, setRecordingTrack] = useState(null);
  const [isCountingIn, setIsCountingIn] = useState(false);
  const [countInProgress, setCountInProgress] = useState(0);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordStreamRef = useRef(null);
  
  // Start audio recording on selected track
  const startRecording = useCallback(async () => {
    const track = tracks.find(t => t.id === selectedTrack);
    if (!track) {
      toast.error('Select a track to record');
      return;
    }
    
    if (track.type === 'instrument') {
      toast.error('Use MIDI recording for instrument tracks');
      return;
    }
    
    try {
      // Detect iOS for optimized audio settings
      const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      
      // iOS-optimized audio constraints
      const audioConstraints = iOS ? {
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          sampleRate: 44100,
        }
      } : { audio: true };
      
      // Request microphone access with optimized constraints
      const stream = await navigator.mediaDevices.getUserMedia(audioConstraints);
      recordStreamRef.current = stream;
      
      // Resume audio context
      if (audioContextRef?.current?.state === 'suspended') {
        await audioContextRef.current.resume();
      }
      
      // Perform count-in if enabled
      if (countInEnabled && countInBeats > 0 && performCountIn) {
        setIsCountingIn(true);
        setCountInProgress(0);
        await performCountIn();
        setIsCountingIn(false);
        setCountInProgress(0);
      }
      
      // Start MediaRecorder
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      recordedChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          recordedChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        // Convert recorded chunks to AudioBuffer
        const blob = new Blob(recordedChunksRef.current, { type: 'audio/webm' });
        const arrayBuffer = await blob.arrayBuffer();
        
        try {
          const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
          
          // Store old values for undo
          const oldAudioBuffer = track.audioBuffer;
          const oldWaveformData = track.waveformData;
          const oldDuration = track.duration;
          
          // Generate waveform
          const waveformData = generateWaveform(audioBuffer);
          
          // Update track
          setTracks(prev => prev.map(t =>
            t.id === selectedTrack
              ? {
                  ...t,
                  audioBuffer,
                  waveformData,
                  duration: audioBuffer.duration,
                  hasAudio: true,
                }
              : t
          ));
          
          // Push undo action
          if (pushUndoAction && ActionTypes) {
            pushUndoAction({
              type: ActionTypes.RECORD_AUDIO,
              trackId: selectedTrack,
              oldAudioBuffer,
              oldWaveformData,
              oldDuration,
              newAudioBuffer: audioBuffer,
              newWaveformData: waveformData,
              newDuration: audioBuffer.duration,
            });
          }
          
          toast.success(`Recorded ${audioBuffer.duration.toFixed(1)}s of audio`);
          
        } catch (decodeError) {
          console.error('Failed to decode recorded audio:', decodeError);
          toast.error('Failed to process recording');
        }
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        recordStreamRef.current = null;
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTrack(selectedTrack);
      toast.info('Recording started');
      
    } catch (error) {
      console.error('Recording error:', error);
      toast.error('Could not access microphone');
    }
  }, [tracks, selectedTrack, audioContextRef, countInEnabled, countInBeats, performCountIn, setTracks, pushUndoAction, ActionTypes]);
  
  // Stop audio recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingTrack(null);
    }
    
    // Ensure stream is stopped
    if (recordStreamRef.current) {
      recordStreamRef.current.getTracks().forEach(track => track.stop());
      recordStreamRef.current = null;
    }
  }, [isRecording]);
  
  // Import audio file to track
  const importAudio = useCallback(async (file, trackId) => {
    if (!file || !audioContextRef?.current) return;
    
    const track = tracks.find(t => t.id === trackId);
    if (!track) {
      toast.error('Track not found');
      return;
    }
    
    try {
      const arrayBuffer = await file.arrayBuffer();
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
      
      // Store old values for undo
      const oldAudioBuffer = track.audioBuffer;
      const oldWaveformData = track.waveformData;
      const oldDuration = track.duration;
      
      // Generate waveform
      const waveformData = generateWaveform(audioBuffer);
      
      // Update track
      setTracks(prev => prev.map(t =>
        t.id === trackId
          ? {
              ...t,
              audioBuffer,
              waveformData,
              duration: audioBuffer.duration,
              hasAudio: true,
              name: t.name === `Track ${t.id + 1}` ? file.name.replace(/\.[^.]+$/, '') : t.name,
            }
          : t
      ));
      
      // Push undo action
      if (pushUndoAction && ActionTypes) {
        pushUndoAction({
          type: ActionTypes.IMPORT_AUDIO,
          trackId,
          oldAudioBuffer,
          oldWaveformData,
          oldDuration,
          newAudioBuffer: audioBuffer,
          newWaveformData: waveformData,
          newDuration: audioBuffer.duration,
        });
      }
      
      toast.success(`Imported: ${file.name}`);
      
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import audio file');
    }
  }, [audioContextRef, tracks, setTracks, pushUndoAction, ActionTypes]);
  
  return {
    // State
    isRecording,
    recordingTrack,
    isCountingIn,
    countInProgress,
    setCountInProgress,
    
    // Methods
    startRecording,
    stopRecording,
    importAudio,
    generateWaveform,
  };
}

export default useRecording;
